import { CompositeCommand, type ICommand } from '../../../core/commands/ICommand';
import type { Scenario } from '../../../core/assistant/stores/scenariosStore';
import {
  createEmptyDelta,
  createScenarioId,
  isDeltaEmpty,
  type NodeUpdateChange,
  type ScenarioDelta,
} from '../../../core/data-profiles/scenarios/ScenarioDelta';
import { buildStatusIndex } from '../../../core/data-profiles/scenarios/ScenarioModel';
import {
  isScenarioRecordable,
  mergeDeltas,
  type CommandDeltaContribution,
} from '../../../core/data-profiles/scenarios/ScenarioRecordable';

type PlanningDelta = ScenarioDelta<Record<string, unknown>, NodeUpdateChange>;
type PlanningContribution = CommandDeltaContribution<Record<string, unknown>, NodeUpdateChange>;

function commandDelta(command: ICommand): PlanningDelta | null {
  if (command instanceof CompositeCommand) {
    let delta = createEmptyDelta<Record<string, unknown>, NodeUpdateChange>();
    for (const child of command.getCommands()) {
      const childDelta = commandDelta(child);
      if (!childDelta) return null;
      delta = mergeDeltas(delta, childDelta);
    }
    return delta;
  }

  if (!isScenarioRecordable(command)) return null;
  return mergeDeltas(
    createEmptyDelta<Record<string, unknown>, NodeUpdateChange>(),
    command.toScenarioDelta() as PlanningContribution,
  );
}

export function createTransientPlanningScenario(
  name = 'Planning draft',
  delta: PlanningDelta = createEmptyDelta<Record<string, unknown>, NodeUpdateChange>(),
): Scenario {
  const now = new Date();
  return {
    id: createScenarioId(`planning-seed-${now.getTime()}-${Math.random().toString(36).slice(2, 11)}`),
    name,
    changes: [],
    delta,
    statusIndex: buildStatusIndex(delta),
    baseSnapshotHash: '',
    createdAt: now,
    updatedAt: now,
    createdBy: 'user',
  };
}

/**
 * Capture the command that opened the master-chart intercept as a detached
 * planning scenario. No command is executed and no source-chart draft is
 * created; the scenario exists only long enough to seed the linked plan.
 */
export function planningScenarioSeedFromCommand(command: ICommand): Scenario | null {
  if (command.canExecute && !command.canExecute()) return null;
  const delta = commandDelta(command);
  if (!delta || isDeltaEmpty(delta)) return null;
  return createTransientPlanningScenario('Planning draft', delta);
}
